import { useState } from "react";
import { Award, BarChart3, Trophy, AlertTriangle } from "lucide-react";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { StatCard } from "@/components/stat-card";
import { useAgentCouncilLeaderboard } from "@/sdk/hooks/use-alpha";
import type { AgentLeaderboardEntry } from "@/sdk/hooks/use-alpha";
import { BenchmarkLeaderboardPanel } from "../components/BenchmarkLeaderboardPanel";
import { BrierScoreWidget } from "../components/BrierScoreWidget";
import { HindcastPanel } from "../components/HindcastPanel";
import { LearnedWeightsWidget } from "../components/LearnedWeightsWidget";

// ---------------------------------------------------------------------------
// Constants — reuse the same agent color scheme as DebateCouncilPage
// ---------------------------------------------------------------------------

const AGENT_BG_COLORS: Record<string, string> = {
  quant: "bg-blue-500",
  skeptic: "bg-amber-500",
  scout: "bg-emerald-500",
  judge: "bg-purple-500",
};

const AGENT_TEXT_COLORS: Record<string, string> = {
  quant: "text-blue-400",
  skeptic: "text-amber-400",
  scout: "text-emerald-400",
  judge: "text-purple-400",
};

const AGENT_LABELS: Record<string, string> = {
  quant: "Quant",
  skeptic: "Skeptic",
  scout: "Scout",
  judge: "Judge",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Tiers mirror the backend's _brier_quality (backend/routers/brier_dashboard.py)
// so this page and the Brier dashboard never grade the same score differently.
function brierQuality(brier: number | null): {
  label: string;
  color: string;
  tone: "success" | "warning" | "danger" | "default";
} {
  if (brier === null) return { label: "N/A", color: "text-white/40", tone: "default" };
  if (brier <= 0.05) return { label: "Exceptional", color: "text-emerald-400", tone: "success" };
  if (brier <= 0.1) return { label: "Excellent", color: "text-green-400", tone: "success" };
  if (brier <= 0.15) return { label: "Good", color: "text-blue-400", tone: "success" };
  if (brier <= 0.25) return { label: "Fair", color: "text-amber-400", tone: "warning" };
  return { label: "Poor", color: "text-red-400", tone: "danger" };
}

// ---------------------------------------------------------------------------
// Small Components
// ---------------------------------------------------------------------------

function AgentBadge({ agentId }: { agentId: string }) {
  const bg = AGENT_BG_COLORS[agentId] ?? "bg-white/20";
  const label = AGENT_LABELS[agentId] ?? agentId;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide text-white ${bg}`}
    >
      {label}
    </span>
  );
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <span className="inline-flex items-center gap-0.5 text-amber-400">
        <Trophy className="h-4 w-4" />
        <span className="text-sm font-bold">1st</span>
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className="inline-flex items-center gap-0.5 text-text/70">
        <Award className="h-4 w-4" />
        <span className="text-sm font-bold">2nd</span>
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className="inline-flex items-center gap-0.5 text-amber-700">
        <Award className="h-4 w-4" />
        <span className="text-sm font-bold">3rd</span>
      </span>
    );
  }
  return <span className="text-sm font-mono text-white/40">#{rank}</span>;
}

// ---------------------------------------------------------------------------
// Brier Score Bar Chart
// ---------------------------------------------------------------------------

function BrierBarChart({ agents }: { agents: AgentLeaderboardEntry[] }) {
  const withScores = agents.filter((a) => a.brier_score !== null);
  if (withScores.length === 0) {
    return (
      <p className="text-xs text-white/30 italic py-4 text-center">
        No Brier score data available yet.
      </p>
    );
  }

  const maxBrier = Math.max(...withScores.map((a) => a.brier_score!), 0.5);

  return (
    <div className="space-y-2">
      {withScores.map((agent) => {
        const pct = ((agent.brier_score ?? 0) / maxBrier) * 100;
        const barColor = AGENT_BG_COLORS[agent.agent_id] ?? "bg-white/30";
        const quality = brierQuality(agent.brier_score);
        return (
          <div key={agent.agent_id} className="flex items-center gap-3">
            <div className="w-16 shrink-0">
              <AgentBadge agentId={agent.agent_id} />
            </div>
            <div className="flex-1 h-6 rounded bg-white/5 relative overflow-hidden">
              <div
                className={`h-full rounded ${barColor} opacity-60 transition-all duration-700`}
                style={{ width: `${Math.max(pct, 3)}%` }}
              />
              <span className="absolute inset-0 flex items-center justify-center text-[0.65rem] font-mono font-bold text-white/90">
                {agent.brier_score !== null
                  ? agent.brier_score.toFixed(4)
                  : "n/a"}
              </span>
            </div>
            <span className={`w-16 text-right text-[0.65rem] font-semibold ${quality.color}`}>
              {quality.label}
            </span>
          </div>
        );
      })}
      <p className="text-[0.6rem] text-white/25 text-center mt-1">
        Lower Brier score = better calibration (0 = perfect, 0.25 = random)
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leaderboard Table
// ---------------------------------------------------------------------------

function LeaderboardTable({
  agents,
  label,
}: {
  agents: AgentLeaderboardEntry[];
  label: string;
}) {
  if (agents.length === 0) {
    return (
      <div className="py-6 text-center">
        <BarChart3 className="mx-auto h-7 w-7 text-white/20" />
        <p className="mt-2 text-sm text-white/40">
          No agent data for {label}.
        </p>
        <p className="text-xs text-white/25">
          Stats appear after the Debate Council runs Tier 3 analysis.
        </p>
      </div>
    );
  }

  // Identify best/worst Brier scores
  const withBrier = agents.filter((a) => a.brier_score !== null);
  const bestBrier = withBrier.length > 0
    ? Math.min(...withBrier.map((a) => a.brier_score!))
    : null;
  const worstBrier = withBrier.length > 0
    ? Math.max(...withBrier.map((a) => a.brier_score!))
    : null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            <th className="px-2 py-2 text-left text-[0.65rem] font-semibold text-white/50 uppercase tracking-wider">
              Rank
            </th>
            <th className="px-2 py-2 text-left text-[0.65rem] font-semibold text-white/50 uppercase tracking-wider">
              Agent
            </th>
            <th className="px-2 py-2 text-left text-[0.65rem] font-semibold text-white/50 uppercase tracking-wider">
              Model
            </th>
            <th className="px-2 py-2 text-right text-[0.65rem] font-semibold text-white/50 uppercase tracking-wider">
              Brier
            </th>
            <th className="px-2 py-2 text-right text-[0.65rem] font-semibold text-white/50 uppercase tracking-wider">
              Accuracy
            </th>
            <th className="px-2 py-2 text-right text-[0.65rem] font-semibold text-white/50 uppercase tracking-wider">
              Votes
            </th>
            <th className="px-2 py-2 text-right text-[0.65rem] font-semibold text-white/50 uppercase tracking-wider">
              Confidence
            </th>
            <th className="px-2 py-2 text-right text-[0.65rem] font-semibold text-white/50 uppercase tracking-wider">
              Avg Shift
            </th>
          </tr>
        </thead>
        <tbody>
          {agents.map((agent) => {
            const isBest =
              bestBrier !== null && agent.brier_score === bestBrier;
            const isWorst =
              worstBrier !== null &&
              agent.brier_score === worstBrier &&
              withBrier.length > 1;
            const quality = brierQuality(agent.brier_score);
            const rowHighlight = isBest
              ? "bg-green-500/5 border-l-2 border-l-green-500/50"
              : isWorst
                ? "bg-red-500/5 border-l-2 border-l-red-500/30"
                : "border-l-2 border-l-transparent";

            return (
              <tr
                key={agent.agent_id}
                className={`border-b border-white/5 hover:bg-white/[0.03] transition ${rowHighlight}`}
              >
                <td className="px-2 py-2.5">
                  <RankBadge rank={agent.rank} />
                </td>
                <td className="px-2 py-2.5">
                  <AgentBadge agentId={agent.agent_id} />
                </td>
                <td className={`px-2 py-2.5 text-[0.7rem] font-mono truncate max-w-[12rem] ${AGENT_TEXT_COLORS[agent.agent_id] ?? "text-white/50"}`}>
                  {agent.model || "-"}
                </td>
                <td className="px-2 py-2.5 text-right">
                  <span
                    className={`font-mono font-bold text-xs ${quality.color}`}
                  >
                    {agent.brier_score !== null
                      ? agent.brier_score.toFixed(4)
                      : "n/a"}
                  </span>
                </td>
                <td className="px-2 py-2.5 text-right">
                  <span
                    className={`font-mono text-xs ${
                      agent.accuracy !== null && agent.accuracy >= 0.7
                        ? "text-green-400"
                        : agent.accuracy !== null && agent.accuracy >= 0.5
                          ? "text-amber-400"
                          : "text-white/50"
                    }`}
                  >
                    {agent.accuracy !== null
                      ? `${(agent.accuracy * 100).toFixed(1)}%`
                      : "n/a"}
                  </span>
                </td>
                <td className="px-2 py-2.5 text-right font-mono text-xs text-white/60">
                  {agent.total_votes}
                </td>
                <td className="px-2 py-2.5 text-right font-mono text-xs text-white/60">
                  {agent.avg_confidence !== null
                    ? `${(agent.avg_confidence * 100).toFixed(0)}%`
                    : "n/a"}
                </td>
                <td className="px-2 py-2.5 text-right">
                  {agent.avg_cross_exam_shift !== null ? (
                    <span
                      className={`font-mono text-xs ${
                        agent.avg_cross_exam_shift > 0.05
                          ? "text-amber-400"
                          : "text-white/50"
                      }`}
                    >
                      {(agent.avg_cross_exam_shift * 100).toFixed(1)}pp
                    </span>
                  ) : (
                    <span className="text-xs text-white/30">-</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

type TimeRange = "30d" | "all";

export function AgentLeaderboardPage() {
  const [range, setRange] = useState<TimeRange>("30d");
  const { data, isLoading, dataUpdatedAt } = useAgentCouncilLeaderboard();

  const leaderboard =
    range === "30d"
      ? data?.leaderboard_30d ?? []
      : data?.leaderboard_all ?? [];
  const totalDebates =
    range === "30d"
      ? data?.total_debates_30d ?? 0
      : data?.total_debates_all ?? 0;

  const isLive = dataUpdatedAt > 0 && Date.now() - dataUpdatedAt < 300_000;

  // Summary stats
  const bestAgent = leaderboard.find((a) => a.rank === 1);
  const avgBrier =
    leaderboard.filter((a) => a.brier_score !== null).length > 0
      ? leaderboard
          .filter((a) => a.brier_score !== null)
          .reduce((s, a) => s + a.brier_score!, 0) /
        leaderboard.filter((a) => a.brier_score !== null).length
      : null;
  const totalVotes = leaderboard.reduce((s, a) => s + a.total_votes, 0);

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header with time range toggle */}
      <Panel
        title="Agent Leaderboard"
        description="Debate Council per-agent performance ranked by Brier score"
        actions={
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg border border-white/10 overflow-hidden">
              <button
                type="button"
                onClick={() => setRange("30d")}
                className={`px-3 py-1 text-xs font-semibold transition ${
                  range === "30d"
                    ? "bg-white/10 text-white"
                    : "text-white/40 hover:text-white/60"
                }`}
              >
                30 Days
              </button>
              <button
                type="button"
                onClick={() => setRange("all")}
                className={`px-3 py-1 text-xs font-semibold transition ${
                  range === "all"
                    ? "bg-white/10 text-white"
                    : "text-white/40 hover:text-white/60"
                }`}
              >
                All Time
              </button>
            </div>
            <div className="flex items-center gap-2 text-xs text-white/40">
              <span
                className={`h-2 w-2 rounded-full ${
                  isLive ? "bg-green-500 animate-pulse" : "bg-muted/50"
                }`}
              />
              {isLive ? "Live" : "Stale"}
            </div>
          </div>
        }
      >
        <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(10rem,1fr))]">
          <StatCard
            label="Total Debates"
            value={String(totalDebates)}
            hint={range === "30d" ? "Last 30 days" : "All time"}
          />
          <StatCard
            label="Total Votes"
            value={String(totalVotes)}
            hint="Blind votes cast"
          />
          <StatCard
            label="Best Agent"
            value={
              bestAgent
                ? `${AGENT_LABELS[bestAgent.agent_id] ?? bestAgent.agent_id}`
                : "n/a"
            }
            tone={bestAgent ? "success" : "default"}
            hint={
              bestAgent?.brier_score !== null && bestAgent?.brier_score !== undefined
                ? `Brier: ${bestAgent.brier_score.toFixed(4)}`
                : "Lowest Brier score"
            }
          />
          <StatCard
            label="Avg Brier Score"
            value={avgBrier !== null ? avgBrier.toFixed(4) : "n/a"}
            tone={
              avgBrier !== null && avgBrier <= 0.15
                ? "success"
                : avgBrier !== null && avgBrier <= 0.25
                  ? "warning"
                  : "default"
            }
            hint="Lower = better calibrated"
          />
        </div>
      </Panel>

      {/* Brier score bar chart */}
      <Panel title="Brier Score Comparison">
        {isLoading ? (
          <LoadingState text="Loading agent scores..." />
        ) : (
          <BrierBarChart agents={leaderboard} />
        )}
      </Panel>

      {/* Leaderboard table */}
      <Panel title={`Leaderboard (${range === "30d" ? "Last 30 Days" : "All Time"})`}>
        {isLoading ? (
          <LoadingState text="Loading leaderboard..." />
        ) : (
          <LeaderboardTable
            agents={leaderboard}
            label={range === "30d" ? "last 30 days" : "all time"}
          />
        )}
      </Panel>

      {/* Warning note when no data */}
      {!isLoading && leaderboard.length === 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
          <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-amber-300">No leaderboard data available</p>
            <p className="text-xs text-white/40 mt-0.5">
              Agent performance data is populated after the alpha pipeline runs
              Tier 3 Debate Council analysis. Run the pipeline to generate debate
              data.
            </p>
          </div>
        </div>
      )}

      {/* Model evaluation: resolved-prediction accuracy, the weights the
          council actually applies, hindcast replays, and the (simulated)
          benchmark suite — all real endpoints, honestly labeled. */}
      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <BrierScoreWidget />
        <LearnedWeightsWidget />
      </div>
      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <HindcastPanel />
        <BenchmarkLeaderboardPanel />
      </div>
    </div>
  );
}
